import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';

import BrandLogo from '../components/BrandLogo';
import { SearchIcon, ShieldIcon, ChatIcon, MailIcon, BoxIcon } from '../components/Icons';
import { trackCatalogueViewed, useTrackError } from '../utils/analytics';
import { writePortalConfig, writeSelectedItem } from '../utils/portalConfig';
import {
  formatMoney,
  getDiscountPercent,
  getItemId,
  getPlanSubtitle,
} from '../utils/format';

import { BASE_URL } from '../App';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const HOW_IT_WORKS = [
  {
    title: 'Pick your plan',
    body: 'Browse the subscriptions your membership unlocks, with the partner price already applied.',
  },
  {
    title: 'Pay securely',
    body: 'Checkout runs on the same gateway as the Fleek app — UPI, cards and netbanking.',
  },
  {
    title: 'Redeem instantly',
    body: 'Your code lands on screen the moment payment clears, with the steps to activate it.',
  },
];

const OfferSkeleton = () => (
  <div className="skeletonGrid">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="skeletonCard" key={i}>
        <div className="shimmer skelLogo" />
        <div className="shimmer skelLine" />
        <div className="shimmer skelLineShort" />
        <div className="shimmer skelPrice" />
        <div className="shimmer skelBtn" />
      </div>
    ))}
  </div>
);

const Marketplace = () => {
  const [portalConfig, setPortalConfig] = useState(null);
  const [portalPlans, setPortalPlans] = useState([]);
  const [error, setError] = useState(null);
  useTrackError('marketplace_load', error);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const fetchPortalData = useCallback(async () => {
    const savedToken = localStorage.getItem('portalToken');

    if (!savedToken) {
      // No token — remember where we were headed and go get one.
      sessionStorage.setItem('returnTo', window.location.pathname || '/marketplace');
      const partnerSlug = localStorage.getItem('partnerSlug');
      navigate(partnerSlug ? `/${partnerSlug}` : '/');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestOptions = {
        method: 'GET',
        headers: { Authorization: `Bearer ${savedToken}` },
        redirect: 'follow',
      };

      const [configResponse, plansResponse] = await Promise.all([
        fetch(`${BASE_URL}/partner/v2/get-portal-configuration`, requestOptions),
        fetch(`${BASE_URL}/partner/v2/get-portal-plans`, requestOptions),
      ]);

      // An expired token reads as a generic HTTP error otherwise, and the user
      // gets stuck on "try again" forever.
      if (configResponse.status === 401 || plansResponse.status === 401) {
        localStorage.removeItem('portalToken');
        sessionStorage.setItem('returnTo', '/marketplace');
        const partnerSlug = localStorage.getItem('partnerSlug');
        navigate(partnerSlug ? `/${partnerSlug}` : '/');
        return;
      }

      if (!configResponse.ok || !plansResponse.ok) {
        throw new Error(
          `Request failed (${configResponse.status}/${plansResponse.status})`
        );
      }

      const [configResult, plansResult] = await Promise.all([
        configResponse.json(),
        plansResponse.json(),
      ]);

      setPortalConfig(configResult);
      writePortalConfig(configResult);
      const loadedPlans = Array.isArray(plansResult.plans) ? plansResult.plans : [];
      setPortalPlans(loadedPlans);

      // The pageview itself is reported by the router for every route.
      trackCatalogueViewed({
        category: 'all',
        planCount: loadedPlans.filter((p) => !p.is_bundle).length,
        bundleCount: loadedPlans.filter((p) => p.is_bundle).length,
      });
    } catch (err) {
      console.error('Failed to load portal data:', err);
      setError(err.message || 'Failed to fetch portal data');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  const handleItemClick = (item) => {
    writeSelectedItem(item);
    const id = getItemId(item);
    navigate(item.is_bundle ? `/bundle-details/${id}` : `/plan-details/${id}`);
  };

  const plans = useMemo(
    () => portalPlans.filter((p) => !p.is_bundle),
    [portalPlans]
  );
  const bundles = useMemo(
    () => portalPlans.filter((p) => p.is_bundle),
    [portalPlans]
  );

  const topDiscount = useMemo(
    () =>
      plans.reduce((max, p) => {
        const d = getDiscountPercent(p.plan_mrp, p.plan_cost);
        return d && d > max ? d : max;
      }, 0),
    [plans]
  );

  /* The single best-value plan gets the hero card, exactly as the marketplace
     landing features its deal of the week. */
  const featured = useMemo(() => {
    if (!plans.length) return null;
    return plans.reduce((best, p) => {
      const d = getDiscountPercent(p.plan_mrp, p.plan_cost) ?? 0;
      const bestD = getDiscountPercent(best.plan_mrp, best.plan_cost) ?? 0;
      return d > bestD ? p : best;
    }, plans[0]);
  }, [plans]);

  const trending = useMemo(
    () => [...new Set(plans.map((p) => p.vendor_name).filter(Boolean))].slice(0, 5),
    [plans]
  );

  const visiblePlans = useMemo(() => {
    const q = query.trim().toLowerCase();
    return plans.filter((p) => {
      if (filter === 'sale' && !getDiscountPercent(p.plan_mrp, p.plan_cost)) {
        return false;
      }
      if (!q) return true;
      return [p.vendor_name, p.plan_name, p.plan_description]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    });
  }, [plans, query, filter]);

  const resetFilters = () => {
    setQuery('');
    setFilter('all');
  };

  const partnerName = portalConfig?.display_name || 'Members';

  if (error) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">!</div>
            <h1 className="statusTitle">We couldn't load your marketplace</h1>
            <p className="statusBody">{error}</p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={fetchPortalData}
              >
                Try again
              </button>
              <a
                className={cx('btn', 'btnMd', 'btnSecondary')}
                href="mailto:help@getfleek.app"
              >
                Contact support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* ---------- Hero ---------- */}
      <section className="hero">
        <div className="heroGlow" />
        <div className="heroInner">
          <div className="heroLeft">
            <div className="heroEyebrow">{partnerName} privileges</div>

            <h1 className="h1">
              Subscriptions you already love.{' '}
              {topDiscount > 0 && (
                <span className="h1Accent">Up to {topDiscount}% off.</span>
              )}
            </h1>

            <p className="heroSub">
              Your {partnerName} membership unlocks partner pricing on streaming,
              music and everyday essentials. Pay once, redeem in seconds.
            </p>

            <div className="heroSearch">
              <SearchIcon className="heroSearchIcon" />
              <input
                className="heroSearchInput"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for a brand or plan"
                aria-label="Search plans"
              />
            </div>

            {trending.length > 0 && (
              <div className="trendingRow">
                <span className="trendingLabel">Popular</span>
                {trending.map((name) => (
                  <button
                    type="button"
                    className="trendingChip"
                    key={name}
                    onClick={() => setQuery(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}

            {!isLoading && (
              <div className="statsRow">
                <div>
                  <div className="statValue">{plans.length}</div>
                  <div className="statLabel">Plans available</div>
                </div>
                {topDiscount > 0 && (
                  <div>
                    <div className="statValue">{topDiscount}%</div>
                    <div className="statLabel">Best saving</div>
                  </div>
                )}
                <div>
                  <div className="statValue">Instant</div>
                  <div className="statLabel">Code delivery</div>
                </div>
              </div>
            )}
          </div>

          {/* Featured deal */}
          {featured && (
            <div className="heroRight">
              <div className="dealCard">
                <div className="dealCardHead">
                  <span className="dealCardLabel">Best value today</span>
                  {getDiscountPercent(featured.plan_mrp, featured.plan_cost) && (
                    <span className="countdownPill">
                      Save {getDiscountPercent(featured.plan_mrp, featured.plan_cost)}%
                    </span>
                  )}
                </div>

                <div className="dealBrandRow">
                  <BrandLogo
                    className="dealLogo"
                    src={featured.vendor_logo_url}
                    name={featured.vendor_name}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="dealBrandName">{featured.vendor_name}</div>
                    {getPlanSubtitle(featured) && (
                      <div className="dealPlanName">{getPlanSubtitle(featured)}</div>
                    )}
                  </div>
                </div>

                <div className="dealPriceRow">
                  <span className="dealPrice">
                    {formatMoney(featured.plan_cost, featured.currency)}
                  </span>
                  {getDiscountPercent(featured.plan_mrp, featured.plan_cost) && (
                    <>
                      <span className="dealMrp">
                        {formatMoney(featured.plan_mrp, featured.currency)}
                      </span>
                      <span className="dealBadge">
                        {getDiscountPercent(featured.plan_mrp, featured.plan_cost)}% OFF
                      </span>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnBrand', 'btnBlock')}
                  style={{ marginTop: 18 }}
                  onClick={() => handleItemClick(featured)}
                >
                  Get this plan
                </button>

                <div className="dealTrust">
                  <ShieldIcon />
                  Secure checkout · Instant delivery
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ---------- Plans ---------- */}
      <section className="shell section" id="plans">
        <div className="sectionHead">
          <div>
            <div className="eyebrow">Available plans</div>
            <h2 className="h2">Everything unlocked for you</h2>
          </div>
          {!isLoading && (
            <div className="sectionMeta">
              {visiblePlans.length} of {plans.length} plans
            </div>
          )}
        </div>

        <div className="chipRow">
          <button
            type="button"
            className={cx('chip', filter === 'all' && 'chipActive')}
            onClick={() => setFilter('all')}
          >
            All plans
          </button>
          {topDiscount > 0 && (
            <button
              type="button"
              className={cx('chip', filter === 'sale' && 'chipActive')}
              onClick={() => setFilter('sale')}
            >
              On sale
            </button>
          )}
        </div>

        {isLoading ? (
          <OfferSkeleton />
        ) : visiblePlans.length ? (
          <div className="dealsGrid">
            {visiblePlans.map((plan) => {
              const discount = getDiscountPercent(plan.plan_mrp, plan.plan_cost);
              return (
                <div className="offerCard" key={getItemId(plan)}>
                  <div className="offerImageWrap">
                    <BrandLogo
                      className="offerImage"
                      src={plan.vendor_logo_url}
                      name={plan.vendor_name}
                    />
                    {discount ? (
                      <span className="offerBadge">
                        <span className="offerBadgeText">{discount}% OFF</span>
                      </span>
                    ) : null}
                  </div>

                  <div className="offerBody">
                    <div className="offerTop">
                      <BrandLogo
                        className="offerLogo"
                        src={plan.vendor_logo_url}
                        name={plan.vendor_name}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="offerBrand">{plan.vendor_name}</div>
                        {getPlanSubtitle(plan) && (
                          <div className="offerCat">{getPlanSubtitle(plan)}</div>
                        )}
                      </div>
                    </div>

                    {plan.plan_description && (
                      <p className="offerDesc">{plan.plan_description}</p>
                    )}

                    <div className="offerPriceRow">
                      <span className="offerPrice">
                        {formatMoney(plan.plan_cost, plan.currency)}
                      </span>
                      {discount ? (
                        <span className="offerMrp">
                          {formatMoney(plan.plan_mrp, plan.currency)}
                        </span>
                      ) : null}
                    </div>

                    <div className="offerSpacer" />

                    <div className="offerActions">
                      <button
                        type="button"
                        className={cx('btn', 'btnSm', 'btnInk', 'btnBlock')}
                        onClick={() => handleItemClick(plan)}
                      >
                        View plan
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="emptyState">
            <div className="emptyTitle">No plans match that search</div>
            <div className="emptyBody">
              Try a different brand, or browse everything available to you.
            </div>
            <button
              type="button"
              className={cx('btn', 'btnMd', 'btnBrand')}
              onClick={resetFilters}
            >
              Show all plans
            </button>
          </div>
        )}
      </section>

      {/* ---------- Bundles ---------- */}
      {!isLoading && bundles.length > 0 && (
        <section className="shell sectionWide" id="bundles">
          <div className="sectionHead">
            <div>
              <div className="eyebrow">Bundles</div>
              <h2 className="h2">More brands, one price</h2>
              <p className="sectionSub">
                Multiple subscriptions bought together, at a lower combined price.
              </p>
            </div>
          </div>

          {bundles.map((bundle) => (
            <div className="bundleCard" key={getItemId(bundle)}>
              <div className="bundleMain">
                <div className="offerTop">
                  <BrandLogo
                    className="dealLogo"
                    src={bundle.product_logo_url}
                    name={bundle.product_name}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="dealBrandName">{bundle.product_name}</div>
                    <div className="dealPlanName">
                      {bundle.plan_list?.length || 0} subscriptions included
                    </div>
                  </div>
                </div>

                {bundle.plan_description && (
                  <p className="offerDesc" style={{ marginTop: 4 }}>
                    {bundle.plan_description}
                  </p>
                )}

                <div className="dealPriceRow">
                  <span className="dealPrice">
                    {formatMoney(bundle.bundle_cost, bundle.currency)}
                  </span>
                </div>

                <button
                  type="button"
                  className={cx('btn', 'btnMd', 'btnBrand')}
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => handleItemClick(bundle)}
                >
                  View bundle
                </button>
              </div>

              {bundle.plan_list?.length > 0 && (
                <div className="bundleIncluded">
                  <div className="bundleIncludedHead" style={{ gridColumn: '1 / -1' }}>
                    What's inside
                  </div>
                  {bundle.plan_list.map((plan, i) => (
                    <div className="bundleItem" key={`${plan.vendor_name}-${i}`}>
                      <BrandLogo
                        className="offerLogo"
                        src={plan.vendor_logo_url}
                        name={plan.vendor_name}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="bundleItemName">{plan.vendor_name}</div>
                        <div className="bundleItemMeta">{plan.plan_name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Nothing at all came back — distinct from "your filters matched nothing". */}
      {!isLoading && portalPlans.length === 0 && (
        <section className="shell section">
          <div className="bundlePanel">
            <span className="comingSoonPill">Coming soon</span>
            <BoxIcon width="28" height="28" style={{ color: 'var(--text-subtle)' }} />
            <p className="bundleBody">
              There are no plans in this marketplace yet. Check back shortly — new
              partner offers are added every week.
            </p>
          </div>
        </section>
      )}

      {/* ---------- How it works ---------- */}
      <section className="shell sectionWide" id="how">
        <div className="sectionHead">
          <h2 className="h2">Three taps, done</h2>
        </div>
        <div className="howGrid">
          {HOW_IT_WORKS.map((step, i) => (
            <div className="howCard" key={step.title}>
              <div className="howStep">{i + 1}</div>
              <div className="howTitle">{step.title}</div>
              <p className="howBody">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Support ---------- */}
      <section className="shell sectionWide">
        <div className="card">
          <div className="cardTitle">Need a hand?</div>
          <p className="howBody" style={{ marginTop: 0 }}>
            Our team is on WhatsApp and email if anything goes wrong with an order.
          </p>
          <div className="supportRow">
            <a
              className={cx('btn', 'btnSm', 'btnSecondary')}
              href="https://wa.me/919148477518"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ChatIcon />
              WhatsApp support
            </a>
            <a className={cx('btn', 'btnSm', 'btnSecondary')} href="mailto:help@getfleek.app">
              <MailIcon />
              help@getfleek.app
            </a>
          </div>
        </div>
      </section>

      <div className="pageFoot" />
    </div>
  );
};

export default Marketplace;
